import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Globe } from 'lucide-react';
import clsx from 'clsx';
import {
  getLocaleFromPathname,
  setStoredLocalePref,
  stripLocalePrefix,
  SUPPORTED_LOCALES,
  withLocale,
  type AppLocale,
} from '../i18n/localeRouting';

type LanguageSwitcherProps = {
  className?: string;
};

export function useLocaleSwitch() {
  const { i18n } = useTranslation('common');
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const current = getLocaleFromPathname(pathname);

  const switchLocale = (locale: AppLocale) => {
    if (locale === current) return;
    setStoredLocalePref(locale);
    void i18n.changeLanguage(locale);
    const bare = stripLocalePrefix(pathname);
    navigate(`${withLocale(bare, locale)}${search}${hash}`);
  };

  return { current, switchLocale };
}

export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const { current, switchLocale } = useLocaleSwitch();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const switchTo = (locale: AppLocale) => {
    setOpen(false);
    switchLocale(locale);
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && rootRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={clsx('landing_lang_switcher', open && 'is-open', className)}
    >
      <div className="landing_lang_switcher_control">
        <Globe className="landing_lang_switcher_globe" size={15} strokeWidth={2} aria-hidden />
        <button
          type="button"
          className="landing_lang_switcher_trigger"
          aria-label={t('nav.language')}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="landing_lang_switcher_trigger_label">{t(`lang.${current}`)}</span>
        </button>
        <ChevronDown
          className="landing_lang_switcher_chevron"
          size={14}
          strokeWidth={2.25}
          aria-hidden
        />
      </div>

      <div
        id={listboxId}
        className={clsx('landing_lang_switcher_menu', open && 'is-open')}
        role="listbox"
        aria-label={t('nav.language')}
        aria-hidden={!open}
      >
        <div className="landing_lang_switcher_menu_inner">
          {SUPPORTED_LOCALES.map((locale) => {
            const selected = locale === current;
            return (
              <button
                key={locale}
                type="button"
                role="option"
                aria-selected={selected}
                tabIndex={open ? 0 : -1}
                className={clsx(
                  'landing_lang_switcher_option',
                  selected && 'is-selected'
                )}
                onClick={() => switchTo(locale)}
              >
                <span className="landing_lang_switcher_option_label">
                  {t(`lang.${locale}`)}
                </span>
                {selected ? (
                  <Check
                    className="landing_lang_switcher_option_check"
                    size={15}
                    strokeWidth={2.5}
                    aria-hidden
                  />
                ) : (
                  <span className="landing_lang_switcher_option_check_spacer" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
